import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@bekebi/domain';
import { ApiError } from '../../api/client';
import { Button } from '../../components/button';
import { useChangePassword } from './api';

/**
 * Change password (/app/account/password), for every signed-in user (POST /api/auth/password).
 * The current password is required. Saving signs this account out of every other browser and
 * closes any open reset link; this browser stays signed in. Passwords exist only in the form.
 */
const formSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password.'),
    newPassword: z
      .string()
      .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
      .max(PASSWORD_MAX_LENGTH, `Use at most ${PASSWORD_MAX_LENGTH} characters.`),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'The new passwords do not match.',
  });
type Form = z.infer<typeof formSchema>;

const LABELS: Record<keyof Form, [string, string]> = {
  currentPassword: ['Current password', 'current-password'],
  newPassword: ['New password', 'new-password'],
  confirmPassword: ['Confirm new password', 'new-password'],
};

export function ChangePasswordPage() {
  const change = useChangePassword();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Form>({ resolver: zodResolver(formSchema) });

  const onSubmit = handleSubmit((values) =>
    change.mutate(
      { currentPassword: values.currentPassword, newPassword: values.newPassword },
      { onSettled: () => reset() },
    ),
  );

  return (
    <div className="max-w-xl">
      <h1>Change password</h1>
      <p className="mt-1 text-sm text-muted">
        Saving signs your account out of every other browser. You stay signed in here.
      </p>
      {change.isSuccess ? (
        <p role="status" className="mt-4 rounded-md bg-success-soft p-3 text-sm text-success">
          Your password was changed. Use the new one the next time you sign in.
        </p>
      ) : null}
      {change.isError ? (
        <p role="alert" className="mt-4 text-sm text-danger">
          {change.error instanceof ApiError
            ? change.error.message
            : 'Your password could not be changed. Try again in a moment.'}
        </p>
      ) : null}
      <form onSubmit={onSubmit} noValidate aria-label="Change password" className="mt-6 space-y-5">
        {(Object.keys(LABELS) as (keyof Form)[]).map((name) => {
          const [label, autoComplete] = LABELS[name];
          const error = errors[name];
          const hint =
            name === 'newPassword'
              ? `At least ${PASSWORD_MIN_LENGTH} characters, different from your current one.`
              : null;
          const describedBy = [hint ? `${name}-hint` : null, error ? `${name}-error` : null]
            .filter(Boolean)
            .join(' ');
          return (
            <div key={name}>
              <label htmlFor={name} className="block text-sm font-medium text-fg-2">
                {label}
              </label>
              <input
                id={name}
                type="password"
                autoComplete={autoComplete}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={describedBy || undefined}
                className="mt-1 block w-full max-w-xl rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
                {...register(name)}
              />
              {hint ? (
                <p id={`${name}-hint`} className="mt-1 text-xs text-muted">
                  {hint}
                </p>
              ) : null}
              {error ? (
                <p id={`${name}-error`} className="mt-1 text-sm text-danger">
                  {error.message}
                </p>
              ) : null}
            </div>
          );
        })}
        <Button type="submit" disabled={change.isPending}>
          {change.isPending ? 'Saving…' : 'Change password'}
        </Button>
      </form>
    </div>
  );
}
